const fontSizeMedium = 'var(--fontSizeMedium, 0.875rem)'
import { useEffect } from 'react'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'astroturf/react'
import { parseQuery } from '@calendly/http-tools'
import { useParams } from 'react-router-dom'

import { useI18n } from 'locales'

import { appendQueryParam } from '../../../utils'
import {
  setPaymentData,
  setStripePaymentError,
  resetPayment,
  setStripePaymentElementReadyForCapture,
  setSaveFormDataForStripeRedirect
} from '../../../actions/payment'
import Badge from '../../payment/stripe/badge'
import { PaymentElementField } from '../../payment/stripe/payment_element_field'
import {
  saveFormDataForStripeRedirectSelector,
  stripeClientSecretSelector,
  stripePaymentElementReadyForCaptureSelector
} from '../../../selectors/payment'
import { StripeRedirectHandler } from '../../enter_details/payment/stripe_redirect_handler'
import _MediumSpacingDiv from "package_stripe_form-MediumSpacingDiv.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/package/payment/package_stripe_form.js?styleId=MediumSpacingDiv";
import _StripeNoteText from "package_stripe_form-StripeNoteText.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/package/payment/package_stripe_form.js?styleId=StripeNoteText";
import _FormContainer from "package_stripe_form-FormContainer.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/package/payment/package_stripe_form.js?styleId=FormContainer";


const MediumSpacingDiv = /*#__PURE__*/styled("div", null, {
  displayName: "MediumSpacingDiv",
  styles: _MediumSpacingDiv
})

const StripeNoteText = /*#__PURE__*/styled("small", null, {
  displayName: "StripeNoteText",
  styles: _StripeNoteText
})

const FormContainer = /*#__PURE__*/styled("div", null, {
  displayName: "FormContainer",
  styles: _FormContainer
})

export const PackageStripeForm = ({ registerApi }) => {
  const stripe = useStripe()
  const elements = useElements()
  const error = useSelector((state) => state.scheduling.payment.error)
  const dispatch = useDispatch()
  const stripeClientSecret = useSelector(stripeClientSecretSelector)
  const paymentElementReadyForCapture = useSelector(stripePaymentElementReadyForCaptureSelector)
  const saveFormDataForStripeRedirect = useSelector(saveFormDataForStripeRedirectSelector)
  const query = parseQuery(window.location.search)
  const { packageUuid } = useParams()
  const isRedirectFlow = query.payment_intent
  const { i18n } = useI18n()

  useEffect(() => {
    const paymentElement = elements?.getElement('payment')

    const handlePaymentMethodTypeChange = ({ value }) => {
      const selectedPaymentMethod = value.type

      if (selectedPaymentMethod !== 'card') {
        dispatch(setSaveFormDataForStripeRedirect(true))
      } else {
        dispatch(setSaveFormDataForStripeRedirect(false))
      }
    }

    if (paymentElement) {
      paymentElement.on('change', handlePaymentMethodTypeChange)
    }
  }, [dispatch, elements])

  useEffect(() => {
    const createPaymentElementPayment = async () => {
      const { error: validationError } = await elements.submit()

      if (validationError) {
        return false
      }

      if (paymentElementReadyForCapture) {
        // Do not confirm PaymentIntent again if it has already been set as ready for capture,
        // but was not captured by the backend because a non-payment related validation failed during purchase
        dispatch(setPaymentData({
          payment_intent_client_secret: stripeClientSecret.value,
          payment_element_flow: true,
          package_uuid: packageUuid
        }))
        return true
      }

      const returnUrl = saveFormDataForStripeRedirect
        ? window.location.href
        : appendQueryParam(window.location.href, 'closeOnLoad=true')

      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl
        },
        redirect: 'if_required'
      })

      if (result.error) {
        dispatch(setStripePaymentError(result.error))
        return false
      }

      if (result.paymentIntent.last_payment_error) {
        dispatch(setStripePaymentError({ code: 'payment_failed' }))
        return false
      }

      if (result.paymentIntent.status === 'requires_capture') {
        dispatch(setPaymentData({
          payment_intent_client_secret: result.paymentIntent.client_secret,
          payment_element_flow: true,
          package_uuid: packageUuid
        }))
        dispatch(setStripePaymentElementReadyForCapture())
        return true
      }

      return false
    }

    const handleCardAction = async (clientSecret) => {
      const result = await stripe.handleCardAction(clientSecret)
      if (result.error) {
        dispatch(setStripePaymentError(result.error))
        return false
      }

      dispatch(setPaymentData({
        id: result.paymentIntent.id,
        additional_payment_auth_done: true,
        package_uuid: packageUuid
      }))
      return true
    }

    const getPaymentData = () => ({
      // Return the current payment data for capture
      payment_intent_client_secret: stripeClientSecret.value,
      payment_element_flow: true,
      package_uuid: packageUuid
    })

    if (!paymentElementReadyForCapture && !isRedirectFlow) dispatch(resetPayment())

    registerApi({
      createPayment: createPaymentElementPayment,
      handleCardAction,
      getPaymentData
    })
  }, [
    dispatch,
    registerApi,
    stripe,
    elements,
    isRedirectFlow,
    paymentElementReadyForCapture,
    saveFormDataForStripeRedirect,
    stripeClientSecret,
    packageUuid
  ])

  if (isRedirectFlow) {
    return (
      <StripeRedirectHandler />
    )
  }

  return (
    <FormContainer>
      <MediumSpacingDiv>
        <PaymentElementField
          error={error}
        />
      </MediumSpacingDiv>
      <StripeNoteText>
        {i18n.t('booking.templates.payment.stripe_note')}
      </StripeNoteText>
      <Badge />
    </FormContainer>
  )
}
